import React, { useState, useEffect } from 'react';
import { 
  CheckCircle, 
  XCircle, 
  AlertTriangle, 
  Clock, 
  BarChart3, 
  TrendingUp,
  Shield,
  Cpu,
  Zap,
  RefreshCw,
  Info
} from 'lucide-react';
import { api } from '../utils/api';
import { formatSafeRelativeTime } from '../utils/date-formatter';
import { PROVIDER_DISPLAY_NAMES } from '../utils/ai-providers';

interface ProviderStatusData {
  providers: Record<string, {
    configured: boolean;
    available_models: string[];
    default_model: string;
    last_24h_usage: {
      total_analyses: number;
      models_used: Array<{
        model: string;
        count: number;
        last_used: string;
        success_rate: number;
      }>;
      error_count: number;
      last_error: string | null;
    };
    relevance_scoring?: {
      total_analyses: number;
      models_used: Array<{
        model: string;
        count: number;
        last_used: string;
        success_rate: number;
        fallback_count: number;
      }>;
      fallback_usage: number;
    };
    status: 'operational' | 'degraded' | 'failed' | 'not_configured';
    is_fallback: boolean;
    health_check?: {
      last_check: string | null;
      last_error: string | null;
      error_message: string | null;
      response_time: number | null;
    };
  }>;
  current_settings: {
    primary_provider: string;
    primary_model: string;
    fallback_enabled: boolean;
    fallback_provider: string;
    fallback_model: string;
    relevance_provider: string;
    relevance_model: string;
  };
  system_health: {
    total_providers_configured: number;
    total_analyses_24h: number;
    fallback_usage_24h: number;
    primary_provider_healthy: boolean;
    relevance_scoring_healthy: boolean;
    total_relevance_analyses_24h: number;
    relevance_fallback_usage_24h: number;
  };
}

export function ProviderStatus() {
  const [status, setStatus] = useState<ProviderStatusData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [lastUpdated, setLastUpdated] = useState<Date>(new Date());
  const [testingProviders, setTestingProviders] = useState<Set<string>>(new Set());
  const [healthCheckResults, setHealthCheckResults] = useState<Record<string, any>>({});
  const [selectedTestModels, setSelectedTestModels] = useState<Record<string, string>>({});

  const loadStatus = async () => {
    try {
      setLoading(true);
      setError(null);
      const data = await api.getProviderStatus();
      setStatus(data);
      setLastUpdated(new Date());
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load provider status');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadStatus();
    // Refresh every 30 seconds
    const interval = setInterval(loadStatus, 30000);
    return () => clearInterval(interval);
  }, []);

  // Fetch models for each provider on initial load
  useEffect(() => {
    if (status && Object.keys(healthCheckResults).length === 0) {
      // Trigger a lightweight health check for each configured provider to get models
      Object.entries(status.providers).forEach(([providerName, provider]) => {
        if (provider.configured && !healthCheckResults[providerName]) {
          triggerHealthCheck(providerName);
        }
      });
    }
  }, [status]);

  const triggerHealthCheck = async (provider: string) => {
    try {
      setTestingProviders(prev => new Set(prev).add(provider));
      
      const response = await api.triggerProviderHealthCheck(provider, selectedTestModels[provider]);
      
      if (response[provider]) {
        setHealthCheckResults(prev => ({
          ...prev,
          [provider]: response[provider]
        }));
        
        // Update the main status with new health check data
        if (status && status.providers[provider]) {
          setStatus({
            ...status,
            providers: {
              ...status.providers,
              [provider]: {
                ...status.providers[provider],
                status: response[provider].status,
                health_check: {
                  last_check: response[provider].lastCheck,
                  last_error: response[provider].lastError,
                  error_message: response[provider].errorMessage,
                  response_time: response[provider].responseTime
                }
              }
            }
          });
        }
      }
    } catch (err) {
      console.error(`Failed to test ${provider}:`, err);
    } finally {
      setTestingProviders(prev => {
        const newSet = new Set(prev);
        newSet.delete(provider);
        return newSet;
      });
    }
  };

  const handleModelSelect = (provider: string, model: string) => {
    setSelectedTestModels(prev => ({
      ...prev,
      [provider]: model
    }));
  };

  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'operational':
        return <CheckCircle className="w-5 h-5 text-green-500" />;
      case 'degraded':
        return <AlertTriangle className="w-5 h-5 text-yellow-500" />;
      case 'not_configured':
        return <XCircle className="w-5 h-5 text-gray-400" />;
      default:
        return <Info className="w-5 h-5 text-gray-400" />;
    }
  };

  const getStatusBadgeClass = (status: string) => {
    switch (status) {
      case 'operational':
        return 'bg-green-100 text-green-800 border-green-200';
      case 'degraded':
        return 'bg-yellow-100 text-yellow-800 border-yellow-200';
      case 'not_configured':
        return 'bg-gray-100 text-gray-600 border-gray-200';
      default:
        return 'bg-gray-100 text-gray-600 border-gray-200';
    }
  };

  const formatProviderName = (name: string) => {
    // Use proper display names from ai-providers, fallback to capitalized name
    return PROVIDER_DISPLAY_NAMES[name as keyof typeof PROVIDER_DISPLAY_NAMES] || 
           name.charAt(0).toUpperCase() + name.slice(1);
  };

  if (loading && !status) {
    return (
      <div className="bg-white rounded-lg shadow-sm border p-6">
        <div className="flex items-center justify-center">
          <RefreshCw className="w-6 h-6 animate-spin text-blue-500" />
          <span className="ml-2 text-gray-600">Loading provider status...</span>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-white rounded-lg shadow-sm border p-6">
        <div className="flex items-center text-red-600">
          <AlertTriangle className="w-5 h-5 mr-2" />
          <span>Error loading provider status: {error}</span>
        </div>
      </div>
    );
  }

  if (!status) return null;

  return (
    <div className="space-y-6">
      {/* System Health Overview */}
      <div className="bg-white rounded-lg shadow-sm border p-6">
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center space-x-3">
            <Shield className="w-6 h-6 text-blue-600" />
            <h3 className="text-lg font-semibold text-gray-900">AI Provider System Status</h3>
          </div>
          <div className="flex items-center space-x-2 text-sm text-gray-500">
            <Clock className="w-4 h-4" />
            <span>Updated {formatSafeRelativeTime(lastUpdated, { addSuffix: true })}</span>
            <button
              onClick={loadStatus}
              className="ml-2 p-1 hover:bg-gray-100 rounded"
              disabled={loading}
            >
              <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
            </button>
          </div>
        </div>

        {/* Health Metrics */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <div className="bg-gray-50 rounded-lg p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600">Configured Providers</p>
                <p className="text-2xl font-bold text-gray-900">
                  {status.system_health.total_providers_configured}/8
                </p>
              </div>
              <Cpu className="w-8 h-8 text-gray-400" />
            </div>
          </div>

          <div className="bg-gray-50 rounded-lg p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600">24h Analyses</p>
                <p className="text-2xl font-bold text-gray-900">
                  {status.system_health.total_analyses_24h}
                </p>
              </div>
              <BarChart3 className="w-8 h-8 text-blue-400" />
            </div>
          </div>

          <div className="bg-gray-50 rounded-lg p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600">Fallback Usage</p>
                <p className="text-2xl font-bold text-gray-900">
                  {status.system_health.fallback_usage_24h}
                </p>
              </div>
              <Shield className="w-8 h-8 text-orange-400" />
            </div>
          </div>

          <div className="bg-gray-50 rounded-lg p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600">Primary Health</p>
                <p className="text-lg font-bold">
                  {status.system_health.primary_provider_healthy ? (
                    <span className="text-green-600 flex items-center">
                      <CheckCircle className="w-5 h-5 mr-1" />
                      Healthy
                    </span>
                  ) : (
                    <span className="text-red-600 flex items-center">
                      <XCircle className="w-5 h-5 mr-1" />
                      Unhealthy
                    </span>
                  )}
                </p>
              </div>
              <Zap className="w-8 h-8 text-green-400" />
            </div>
          </div>
        </div>
      </div>

      {/* Relevance Scoring Status */}
      <div className="bg-white rounded-lg shadow-sm border p-6">
        <div className="flex items-center space-x-3 mb-4">
          <Zap className="w-6 h-6 text-purple-600" />
          <h3 className="text-lg font-semibold text-gray-900">Relevance Scoring Status</h3>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
          <div className="bg-gray-50 rounded-lg p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600">24h Relevance Analyses</p>
                <p className="text-2xl font-bold text-gray-900">
                  {status.system_health.total_relevance_analyses_24h || 0}
                </p>
              </div>
              <BarChart3 className="w-8 h-8 text-purple-400" />
            </div>
          </div>

          <div className="bg-gray-50 rounded-lg p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600">Fallback Usage</p>
                <p className="text-2xl font-bold text-gray-900">
                  {status.system_health.relevance_fallback_usage_24h || 0}
                </p>
              </div>
              <Shield className="w-8 h-8 text-orange-400" />
            </div>
          </div>

          <div className="bg-gray-50 rounded-lg p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600">Current Provider</p>
                <p className="text-base font-medium text-gray-900">
                  {formatProviderName(status.current_settings.relevance_provider)}
                </p>
                <p className="text-sm text-gray-500">
                  {status.current_settings.relevance_model}
                </p>
              </div>
              <Cpu className="w-8 h-8 text-gray-400" />
            </div>
          </div>
        </div>

        {/* Relevance Provider Health Alert */}
        {!status.system_health.relevance_scoring_healthy && (
          <div className="bg-red-50 border border-red-200 rounded-lg p-4">
            <div className="flex items-center space-x-2">
              <AlertTriangle className="w-5 h-5 text-red-600" />
              <div>
                <p className="text-sm font-medium text-red-800">
                  Relevance Scoring Provider Issue Detected
                </p>
                <p className="text-sm text-red-700">
                  The configured relevance provider ({formatProviderName(status.current_settings.relevance_provider)}) 
                  appears to be experiencing issues. Analyses may be falling back to Cloudflare AI or basic scoring.
                </p>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Current Configuration */}
      <div className="bg-white rounded-lg shadow-sm border p-6">
        <h3 className="text-lg font-semibold text-gray-900 mb-4">Current Configuration</h3>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <p className="text-sm text-gray-600">Primary Provider</p>
            <p className="text-base font-medium text-gray-900">
              {formatProviderName(status.current_settings.primary_provider)} - {status.current_settings.primary_model}
            </p>
          </div>
          <div>
            <p className="text-sm text-gray-600">Fallback Settings</p>
            <p className="text-base font-medium text-gray-900">
              {status.current_settings.fallback_enabled ? (
                <span className="text-green-600">
                  Enabled - {formatProviderName(status.current_settings.fallback_provider)}
                </span>
              ) : (
                <span className="text-red-600">Disabled</span>
              )}
            </p>
          </div>
        </div>
      </div>

      {/* Provider Details */}
      <div className="bg-white rounded-lg shadow-sm border p-6">
        <h3 className="text-lg font-semibold text-gray-900 mb-4">Provider Details</h3>
        <div className="space-y-4">
          {Object.entries(status.providers).map(([providerName, provider]) => (
            <div key={providerName} className="border rounded-lg p-4">
              <div className="flex items-center justify-between mb-3">
                <div className="flex items-center space-x-3">
                  {getStatusIcon(provider.status)}
                  <h4 className="text-base font-medium text-gray-900">
                    {formatProviderName(providerName)}
                    {provider.is_fallback && (
                      <span className="ml-2 text-xs bg-blue-100 text-blue-800 px-2 py-1 rounded-full">
                        Fallback
                      </span>
                    )}
                    {providerName === status.current_settings.primary_provider && (
                      <span className="ml-2 text-xs bg-purple-100 text-purple-800 px-2 py-1 rounded-full">
                        Primary
                      </span>
                    )}
                  </h4>
                </div>
                <div className="flex items-center space-x-2">
                  <span className={`px-3 py-1 text-xs font-medium rounded-full border ${getStatusBadgeClass(provider.status)}`}>
                    {provider.status.replace('_', ' ').toUpperCase()}
                  </span>
                </div>
              </div>

              {/* Model selection and test button */}
              {provider.configured && (
                <div className="mt-3 p-3 bg-gray-50 rounded-lg">
                  <div className="flex items-center justify-between space-x-4">
                    <div className="flex-1">
                      <label className="text-xs font-medium text-gray-700 mb-1 block">Select Model to Test:</label>
                      <select
                        value={selectedTestModels[providerName] || provider.default_model}
                        onChange={(e) => handleModelSelect(providerName, e.target.value)}
                        className="w-full px-3 py-1.5 text-sm border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
                        disabled={testingProviders.has(providerName)}
                      >
                        {/* Use health check results for models if available, otherwise use provider models */}
                        {(healthCheckResults[providerName]?.availableModels || provider.available_models).map(model => (
                          <option key={model} value={model}>{model}</option>
                        ))}
                      </select>
                    </div>
                    <button
                      onClick={() => triggerHealthCheck(providerName)}
                      disabled={testingProviders.has(providerName)}
                      className="px-4 py-2 text-sm font-medium text-white bg-blue-600 rounded-md hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed flex items-center space-x-2"
                    >
                      <RefreshCw className={`w-4 h-4 ${testingProviders.has(providerName) ? 'animate-spin' : ''}`} />
                      <span>{testingProviders.has(providerName) ? 'Testing...' : 'Test Selected Model'}</span>
                    </button>
                  </div>
                </div>
              )}

              <div className="grid grid-cols-1 md:grid-cols-3 gap-4 text-sm mt-4">
                <div>
                  <p className="text-gray-600">Configuration</p>
                  <p className="font-medium">
                    {provider.configured ? 'API Key Configured' : 'Not Configured'}
                  </p>
                </div>

                <div>
                  <p className="text-gray-600">24h Usage</p>
                  <p className="font-medium">
                    {provider.last_24h_usage.total_analyses} analyses
                    {provider.last_24h_usage.error_count > 0 && (
                      <span className="text-red-600 ml-2">
                        ({provider.last_24h_usage.error_count} errors)
                      </span>
                    )}
                  </p>
                </div>

                <div>
                  <p className="text-gray-600">Default Model</p>
                  <p className="font-medium text-xs">{provider.default_model}</p>
                </div>
              </div>

              {provider.last_24h_usage.models_used.length > 0 && (
                <div className="mt-3 pt-3 border-t">
                  <p className="text-sm text-gray-600 mb-2">Model Usage (24h)</p>
                  <div className="space-y-1">
                    {provider.last_24h_usage.models_used.map((modelUsage, idx) => (
                      <div key={idx} className="flex items-center justify-between text-xs">
                        <span className="text-gray-700">{modelUsage.model}</span>
                        <div className="flex items-center space-x-2">
                          <span className="text-gray-500">{modelUsage.count} calls</span>
                          <span className="text-gray-400">•</span>
                          <span className={`font-medium ${
                            modelUsage.success_rate >= 0.9 ? 'text-green-600' :
                            modelUsage.success_rate >= 0.7 ? 'text-yellow-600' :
                            'text-red-600'
                          }`}>
                            {(modelUsage.success_rate * 100).toFixed(0)}% success
                          </span>
                          {modelUsage.last_used && (
                            <>
                              <span className="text-gray-400">•</span>
                              <span className="text-gray-500">
                                {formatSafeRelativeTime(modelUsage.last_used, { addSuffix: true })}
                              </span>
                            </>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Relevance Scoring Section */}
              {provider.relevance_scoring && provider.relevance_scoring.total_analyses > 0 && (
                <div className="mt-3 pt-3 border-t">
                  <p className="text-sm text-gray-600 mb-2">Relevance Scoring (24h)</p>
                  <div className="grid grid-cols-2 gap-4 text-xs">
                    <div>
                      <span className="text-gray-700">Total analyses: </span>
                      <span className="font-medium">{provider.relevance_scoring.total_analyses}</span>
                    </div>
                    <div>
                      <span className="text-gray-700">Fallback usage: </span>
                      <span className={`font-medium ${provider.relevance_scoring.fallback_usage > 0 ? 'text-orange-600' : 'text-green-600'}`}>
                        {provider.relevance_scoring.fallback_usage}
                      </span>
                    </div>
                  </div>
                  {provider.relevance_scoring.models_used.length > 0 && (
                    <div className="space-y-1 mt-2">
                      {provider.relevance_scoring.models_used.map((modelUsage, idx) => (
                        <div key={idx} className="flex items-center justify-between text-xs">
                          <span className="text-gray-700">{modelUsage.model}</span>
                          <div className="flex items-center space-x-2">
                            <span className="text-gray-500">{modelUsage.count} relevance calls</span>
                            {modelUsage.fallback_count > 0 && (
                              <>
                                <span className="text-gray-400">•</span>
                                <span className="text-orange-600">{modelUsage.fallback_count} fallbacks</span>
                              </>
                            )}
                            <span className="text-gray-400">•</span>
                            <span className={`font-medium ${
                              modelUsage.success_rate >= 0.9 ? 'text-green-600' :
                              modelUsage.success_rate >= 0.7 ? 'text-yellow-600' :
                              'text-red-600'
                            }`}>
                              {(modelUsage.success_rate * 100).toFixed(0)}% success
                            </span>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              )}

              {/* Show health check error if exists */}
              {provider.health_check && provider.health_check.error_message && (
                <div className="mt-3 pt-3 border-t">
                  <div className="flex items-start space-x-2 text-sm text-red-600">
                    <AlertTriangle className="w-4 h-4 mt-0.5 flex-shrink-0" />
                    <div>
                      <p className="font-medium">Health Check Failed</p>
                      <p className="text-red-500">{provider.health_check.error_message}</p>
                      {provider.health_check.last_check && (
                        <p className="text-xs text-red-400 mt-1">
                          Last checked: {formatSafeRelativeTime(provider.health_check.last_check, { addSuffix: true })}
                        </p>
                      )}
                    </div>
                  </div>
                </div>
              )}

              {/* Show health check results with model info */}
              {healthCheckResults[providerName] && (
                <div className="mt-3 pt-3 border-t">
                  <div className="text-sm">
                    <p className="font-medium text-gray-700 mb-2">Recent Health Check Result:</p>
                    <div className="bg-gray-50 rounded p-3 space-y-2">
                      <div className="flex items-center justify-between">
                        <span className="text-gray-600">Status:</span>
                        <span className={`font-medium ${
                          healthCheckResults[providerName].status === 'operational' ? 'text-green-600' :
                          healthCheckResults[providerName].status === 'degraded' ? 'text-yellow-600' :
                          'text-red-600'
                        }`}>
                          {healthCheckResults[providerName].status.toUpperCase()}
                        </span>
                      </div>
                      {healthCheckResults[providerName].testModel && (
                        <div className="flex items-center justify-between">
                          <span className="text-gray-600">Tested Model:</span>
                          <span className="font-medium text-gray-900 text-xs">
                            {healthCheckResults[providerName].testModel}
                          </span>
                        </div>
                      )}
                      {healthCheckResults[providerName].responseTime && (
                        <div className="flex items-center justify-between">
                          <span className="text-gray-600">Response Time:</span>
                          <span className="font-medium text-gray-900">
                            {healthCheckResults[providerName].responseTime}ms
                          </span>
                        </div>
                      )}
                      {healthCheckResults[providerName].availableModels && (
                        <div>
                          <span className="text-gray-600">Available Models:</span>
                          <div className="mt-1 flex flex-wrap gap-1">
                            {healthCheckResults[providerName].availableModels.slice(0, 5).map((model: string, idx: number) => (
                              <span key={idx} className="text-xs bg-blue-100 text-blue-700 px-2 py-0.5 rounded">
                                {model}
                              </span>
                            ))}
                            {healthCheckResults[providerName].availableModels.length > 5 && (
                              <span className="text-xs text-gray-500">
                                +{healthCheckResults[providerName].availableModels.length - 5} more
                              </span>
                            )}
                          </div>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              )}

              {provider.last_24h_usage.error_count > 0 && provider.last_24h_usage.last_error && (
                <div className="mt-3 pt-3 border-t">
                  <div className="flex items-center space-x-2 text-sm text-orange-600">
                    <AlertTriangle className="w-4 h-4" />
                    <span>Usage errors (24h): {provider.last_24h_usage.error_count}</span>
                  </div>
                </div>
              )}
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}